// =============================================================================
// useCloseAfterTick — close a capture sheet AFTER its saved tick has shown,
// and refuse a second save while it waits (farm audit SC-11, M-01 fix round 1).
//
// "Log scout" closed the sheet in the same tick the save landed, and the saved
// tick lives in the sheet's own overlay — so it unmounted before it painted.
// Holding the sheet open until the overlay's onDismiss (~1.2 s, with a
// fallback so a tick that never paints can never hold it open) fixed that, and
// opened a new hole: for that second the form was still MOUNTED and
// submittable. The overlay blocks taps, not the keyboard, and <Modal> refuses a
// submit only while `isPending` — false once the save has landed. A second
// Enter wrote a second observation with a fresh id (two breach alerts for one
// find). So once a closing save has landed, `guardSubmit` swallows every
// further submit, and `closing` disables the buttons.
//
//   const tick = useCloseAfterTick(onSaved)
//   onSuccess: () => { …; tick.armClose() }
//   onSubmit={() => tick.guardSubmit(() => save.mutate())}
//   overlay={{ …, onDismiss: tick.closeAfterTick }}
// =============================================================================

import { useCallback, useRef, useState } from 'react'

export const CLOSE_FALLBACK_MS = 2500

export function useCloseAfterTick(onSaved: () => void, fallbackMs = CLOSE_FALLBACK_MS) {
  // The overlay re-arms its timer whenever onDismiss changes, and a live walk
  // re-renders the form every fix — so every callback here is stable.
  const onSavedRef = useRef(onSaved)
  onSavedRef.current = onSaved
  const armedRef = useRef(false)
  const landedRef = useRef(false)
  const [closing, setClosing] = useState(false)

  const closeAfterTick = useCallback(() => {
    if (!armedRef.current) return
    armedRef.current = false
    onSavedRef.current()
  }, [])

  const armClose = useCallback(() => {
    armedRef.current = true
    landedRef.current = true
    setClosing(true)
    window.setTimeout(closeAfterTick, fallbackMs)
  }, [closeAfterTick, fallbackMs])

  /** Run `submit` unless a closing save has already landed. Read from a ref,
   *  not `closing`: a second Enter can arrive before the re-render. */
  const guardSubmit = useCallback((submit: () => void) => {
    if (landedRef.current) return
    submit()
  }, [])

  return { closing, armClose, closeAfterTick, guardSubmit }
}
